import { FC, useContext, useEffect, useRef, useState } from 'react';

import {
  Button,
  DefaultImageIcon,
  H1,
  Modal,
  Preloader,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';

import { ITemplateModalProps } from './interfaces';
import s from './TemplateModal.module.scss';

export const TemplateModal: FC<ITemplateModalProps> = ({
  onClose,
  templateData,
  loading,
  setOpenTemplatesPlay,
  onApplyTemplate,
}) => {
  const { t } = useContext(LocalizationContext);

  const [expanded, setExpanded] = useState<boolean>(false);
  const [showButton, setShowButton] = useState<boolean>(false);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (textRef?.current) {
      const isOverflowing = textRef.current.scrollHeight > 154;
      setShowButton(isOverflowing);
    }
  }, [templateData?.description, textRef?.current]);

  return (
    <Modal className={s.modal} onClose={onClose}>
      <Preloader loading={loading}>
        <div className={s.container}>
          <div className={s.info}>
            <div className={s.left}>
              <span className={s.title}>{t('cover')}</span>
              {templateData?.main_picture ? (
                <img
                  alt="preview"
                  className={s.image}
                  src={templateData?.main_picture}
                />
              ) : (
                <div className={s.placeholder}>
                  <DefaultImageIcon color="secondary" size="xxl" />
                </div>
              )}
              <span className={s.age}>
                {templateData?.age_rating ? templateData?.age_rating : 0}+
              </span>
            </div>

            <div className={s.right}>
              <div className={s.main_info}>
                <H1>{templateData?.title}</H1>
                <div className={s.description_block}>
                  <span className={s.title}>{t('description')}</span>
                  <span
                    ref={textRef}
                    className={cn(s.text, expanded ? s.expanded : s.collapsed)}
                  >
                    {templateData?.description}
                  </span>
                </div>
                {showButton && (
                  <Button
                    variant="ghost"
                    className={s.showMoreButton}
                    iconName={!expanded ? 'BottomIcon' : 'TopIcon'}
                    iconPosition="right"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(!expanded);
                    }}
                  >
                    {expanded ? t('hide') : t('show.more')}
                  </Button>
                )}
              </div>

              <div className={s.field}>
                <div className={s.block}>
                  <span className={s.title}>{t('application.args')}</span>
                  {templateData?.args ? (
                    <span className={s.text}>{templateData?.args}</span>
                  ) : (
                    <span className={cn(s.text, s.text_disabled)}>
                      {t('templates.modal.arg')}
                    </span>
                  )}
                </div>
                <div className={s.bottom}>
                  <div className={s.block}>
                    <span className={s.title}>
                      {t('application.age.title')}
                    </span>
                    <span className={s.text}>{templateData?.age_rating}+</span>
                  </div>
                  <div className={s.block}>
                    <span className={s.title}>{t('template.modal.admin')}</span>
                    <span className={s.text}>
                      {templateData?.run_as_admin ? t('yes') : t('no')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {templateData?.screenshots?.length && (
            <div className={s.screenshots}>
              <span className={s.title}>
                {t('screenshots', templateData?.screenshots?.length)}
              </span>

              <div className={s.screenshots_block}>
                {templateData?.screenshots?.map((screen) => (
                  <img
                    className={s.screenshot}
                    src={screen}
                    alt="screenshots"
                    key={screen}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className={s.footer}>
          <div className={s.buttons_block}>
            <Button
              onClick={() => {
                setOpenTemplatesPlay(true);
                onClose();
              }}
              variant="secondary"
              iconName="TemplateIcon"
            >
              {t('all.pattern')}
            </Button>
            <Button
              iconName="CheckIcon"
              onClick={() => {
                if (templateData) {
                  onApplyTemplate(templateData);
                  onClose();
                }
              }}
            >
              {t('action.pattern')}
            </Button>
          </div>
          <span className={s.title}>{t('pattern.modal.bottom.text')}</span>
        </div>
      </Preloader>
    </Modal>
  );
};
